import {
  FileMetadataContext,
  useRecordFileMetadata
} from '@Pages/pageRuntime/context/FileMetadataContext';
import { useResolvedResource } from '@Pages/pageRuntime/context/SdkResourceResolverContext';
import type { RuntimeCollectionResult } from '@Pages/pageRuntime/fileMetadataStore';
import { toApiError } from '@Pages/pageRuntime/sdk/apiErrors';
import { assertResourceMethodAvailable } from '@Pages/pageRuntime/sdk/endpointGating';
import { normalizeSort } from '@Pages/pageRuntime/sdk/sortNormalization';
import type {
  ApiError,
  ResourceCollectionResult,
  ResourceRecord,
  ResourceSearchBody,
  SdkRecordsHandle,
  SdkReferenceRecord,
  SdkReferenceRelationPath,
  SdkResourceQueryReference,
  SdkUseRecordsOptions
} from '@Pages/pageRuntime/sdk/types';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';

import { usePollCountdown, usePollRefetchInterval } from './pollInterval';

/** Shared empty page so a loading `data` array keeps a stable identity across renders. */
const NO_RECORDS: ResourceRecord[] = [];

/** The records this hook returns. `pollInterval` callbacks must see the same array. */
function toRecords(result: ResourceCollectionResult | undefined): ResourceRecord[] {
  return result?.data ?? NO_RECORDS;
}

// A null option or an empty sort list means "not configured" and is skipped like an
// omitted one, so saved view state (e.g. a shared filter's sorts) can be spread in
// without null checks.
export function buildSearchBody(options: SdkUseRecordsOptions | undefined): ResourceSearchBody {
  const body: ResourceSearchBody = {};

  if (options?.relations && options.relations.length > 0) {
    body.relations = options.relations;
  }
  if (options?.filter != null) {
    body.$filter = options.filter;
  }
  const sort = options?.sort == null ? {} : normalizeSort(options.sort);
  if (Object.keys(sort).length > 0) {
    body.$sort = sort;
  }
  if (options?.search) {
    body.$search = options.search;
  }
  if (options?.page != null) {
    body.page = options.page;
  }
  if (options?.perPage != null) {
    body.per_page = options.perPage;
  }
  if (options?.scope != null) {
    body.$scope = options.scope;
  }
  if (options?.collapse != null) {
    body.$collapse = options.collapse;
  }

  return body;
}

/**
 * Reactive wrapper around `resource.urls.search` for fetching a page of records —
 * the collection twin of `useRecord`, and the page counterpart to
 * `@Core/api/RecordApi.useResourceRecords`. Search-backed rather than list-backed
 * because the list endpoint evaluates neither `$filter` nor `$sort`.
 *
 * Exposed via `Sdk.hooks.useRecords(resource, options)`.
 *
 * A nullish `resource` keeps the hook idle instead of throwing, so pages may pass a
 * resource that is only resolved after a later render. A resource id or slug string is
 * resolved through `SdkResourceResolverContext`; a reference the viewer cannot access
 * also keeps the hook idle.
 */
export function useRecords<
  TReference extends SdkResourceQueryReference<'search'>,
  const TRelations extends readonly SdkReferenceRelationPath<NoInfer<TReference>>[] = readonly []
>(
  resource: TReference,
  options?: SdkUseRecordsOptions<TRelations, SdkReferenceRecord<TReference, TRelations>>
): SdkRecordsHandle<SdkReferenceRecord<TReference, TRelations>>;
export function useRecords(
  reference: SdkResourceQueryReference<'search'>,
  options?: SdkUseRecordsOptions
): SdkRecordsHandle {
  const fileStore = useContext(FileMetadataContext);
  const resource = useResolvedResource(reference);
  const queryClient = useQueryClient();
  const poll = usePollRefetchInterval<ResourceCollectionResult, ResourceRecord[]>(
    options?.pollInterval,
    toRecords
  );

  const body = buildSearchBody(options);
  const searchUrl = resource && 'urls' in resource ? resource.urls.search : undefined;
  const resourceName = resource?.name ?? '';
  const disabledEndpoints = resource?.disabled_data_api_endpoints ?? [];
  // `records` shares the query-key head every record mutation invalidates, so a
  // create/update/delete through a resource handle refreshes this page of records.
  const queryKey = [
    'records',
    resource?.id,
    body,
    searchUrl,
    resourceName,
    disabledEndpoints,
    fileStore?.scope
  ];
  const enabled = (options?.enabled ?? true) && searchUrl !== undefined;

  const query = useQuery<RuntimeCollectionResult, ApiError>({
    enabled,
    queryKey,
    refetchInterval: poll.refetchInterval,
    queryFn: () => {
      assertResourceMethodAvailable(
        { name: resourceName, disabled_data_api_endpoints: disabledEndpoints },
        'search'
      );

      return axios
        .post(searchUrl as string, body)
        .then((response) => fileStore?.receive(response.data) ?? response.data)
        .catch((error) => {
          throw toApiError(error);
        });
    },
    // Keep the previous page rendered while a new page, filter, or sort loads.
    placeholderData: keepPreviousData
  });
  const refreshIn = usePollCountdown(poll, query, enabled);

  const files = useRecordFileMetadata(query.data ? [query.data] : []);

  return {
    files,
    data: toRecords(query.data),
    pagination: query.data?.pagination,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refreshIn,
    invalidate: () => queryClient.invalidateQueries({ queryKey })
  };
}
